import { ScheduleFollowupModal } from "../../components/ScheduleFollowupModal";
import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "../../components/DashboardLayout";
import { useClinicLinks } from "./ClinicAdminPages";
import { ActionPromptModal } from "../../components/ActionPromptModal";
import { PatientSearchSelect } from "../../components/PatientSearchSelect";
import { FollowupConfirmModal, FollowupConvertModal } from "../../components/FollowupConvertModal";
import api from "../../lib/api";
import { toast } from "sonner";
import { useCan } from "../../lib/permissions";
import {
  Search, Plus, Pencil, Eye, ArrowLeft, Save, X, RefreshCw, Trash2,
  PhoneCall, CalendarClock, AlertTriangle, CheckCircle2, XCircle, Activity,
  ShieldCheck, User as UserIcon, Stethoscope, Clock, Filter, MessageCircle,
  Mail, MessageSquare, ChevronRight, Repeat, Flag,
} from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  contacted: "bg-blue-100 text-blue-800",
  confirmed_pending_booking: "bg-violet-100 text-violet-800",
  completed: "bg-emerald-100 text-emerald-800",
  missed: "bg-red-100 text-red-800",
  cancelled: "bg-slate-200 text-slate-700",
  converted_to_appointment: "bg-mg-medical/10 text-mg-medical",
  deleted: "bg-red-50 text-red-500 line-through",
};
const PRIORITY_STYLES = {
  low: "text-slate-500",
  normal: "text-slate-700",
  high: "text-amber-600 font-semibold",
  urgent: "text-red-600 font-bold",
};
const CHANNEL_ICONS = {
  phone: PhoneCall, sms: MessageSquare, whatsapp: MessageCircle, email: Mail,
  in_person: UserIcon, other: MessageCircle,
};

const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return s; }
};
const fmtDateTime = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return s; }
};
const isOverdue = (due, status) => {
  if (!due || status === "completed" || status === "cancelled" || status === "converted_to_appointment") return false;
  try { return new Date(due) < new Date(new Date().toDateString()); } catch { return false; }
};

// ============================================================
// LIST PAGE
// ============================================================
export function ClinicFollowupsListPage({ workspaceLinks, homeBase="/clinic-admin" }) {
  const clinicLinks = useClinicLinks();
  const links = workspaceLinks || clinicLinks;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [dueWindow, setDueWindow] = useState("");
  const [patientFilter, setPatientFilter] = useState("");
  const [doctorFilter, setDoctorFilter] = useState("");
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [showCreate, setShowCreate] = useState(false);
  const [bulkReasonModal, setBulkReasonModal] = useState(null);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const canCreate = useCan("followups.create");
  const canBulk = useCan("followups.bulk_actions");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.append("q", q);
      if (statusFilter) params.append("status_filter", statusFilter);
      if (priorityFilter) params.append("priority", priorityFilter);
      if (dueWindow) params.append("due_window", dueWindow);
      if (patientFilter) params.append("patient_id", patientFilter);
      if (doctorFilter) params.append("doctor_id", doctorFilter);
      const r = await api.get(`/clinic/followups-x?${params.toString()}`);
      setRows(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    setLoading(false);
  }, [q, statusFilter, priorityFilter, dueWindow, patientFilter, doctorFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    Promise.all([
      api.get("/clinic/patients-x", { params: { limit: 500 } }),
      api.get("/clinic/doctors-x", { params: { limit: 500 } }),
    ]).then(([patientResponse, doctorResponse]) => {
      setPatients(patientResponse.data?.items || patientResponse.data || []);
      setDoctors(doctorResponse.data?.items || doctorResponse.data || []);
    }).catch(() => {});
  }, []);

  const toggleSelect = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };

  const runBulk = async (action, extra = {}) => {
    if (!selected.size) return toast.error("No follow-ups selected");
    setBulkReasonModal({ action, extra, ids: [...selected] });
  };

  const submitBulk = async (reason) => {
    if (!bulkReasonModal) return;
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      toast.error("Reason is required");
      return;
    }
    setBulkSubmitting(true);
    try {
      const r = await api.post("/clinic/followups-x/bulk", {
        followup_ids: bulkReasonModal.ids,
        action: bulkReasonModal.action,
        reason: trimmedReason,
        ...bulkReasonModal.extra,
      });
      toast.success(`${r.data.ok.length} ok, ${r.data.failed.length} failed`);
      setSelected(new Set());
      setBulkReasonModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk failed");
    }
    setBulkSubmitting(false);
  };

  return (
    <DashboardLayout links={links} title="Follow-ups">
      <div className="mg-card mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className="mg-input pl-9 w-full" placeholder="Search reason / notes…"
              value={q} onChange={(e) => setQ(e.target.value)} data-testid="followups-search" />
          </div>
          <PatientSearchSelect patients={patients} value={patientFilter} onChange={setPatientFilter} label="" className="min-w-[240px] flex-1" testid="followups-patient-filter" />
          <select aria-label="Doctor filter" className="mg-input text-sm" value={doctorFilter} onChange={(e) => setDoctorFilter(e.target.value)} data-testid="followups-doctor-filter">
            <option value="">All doctors</option>
            {doctors.map((doctor) => <option key={doctor.doctor_id} value={doctor.doctor_id}>{doctor.name}</option>)}
          </select>
          <select className="mg-input text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="followups-status-filter">
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="contacted">Contacted</option>
            <option value="completed">Completed</option>
            <option value="missed">Missed</option>
            <option value="cancelled">Cancelled</option>
            <option value="converted_to_appointment">Converted</option>
          </select>
          <select className="mg-input text-sm" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} data-testid="followups-priority-filter">
            <option value="">All priorities</option>
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
          <select className="mg-input text-sm" value={dueWindow} onChange={(e) => setDueWindow(e.target.value)} data-testid="followups-due-filter">
            <option value="">Any due date</option>
            <option value="overdue">Overdue</option>
            <option value="today">Due today</option>
            <option value="week">Due this week</option>
            <option value="month">Due this month</option>
          </select>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={load} className="mg-btn-secondary text-sm" data-testid="followups-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            {canCreate && (
              <button onClick={() => setShowCreate(true)} className="mg-btn-primary text-sm" data-testid="add-followup-btn">
                <Plus className="w-4 h-4" /> New follow-up
              </button>
            )}
          </div>
        </div>
      </div>

      {selected.size > 0 && canBulk && (
        <div className="mg-card mb-4 bg-mg-medical/5 border-mg-medical/20" data-testid="followup-bulk-bar">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm font-semibold text-mg-navy">{selected.size} selected</div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => runBulk("mark_completed")} className="mg-btn-secondary text-xs" data-testid="fu-bulk-complete">Mark completed</button>
              <button onClick={() => runBulk("mark_missed")} className="mg-btn-secondary text-xs" data-testid="fu-bulk-missed">Mark missed</button>
              <button onClick={() => runBulk("cancel")} className="mg-btn-secondary text-xs" data-testid="fu-bulk-cancel">Cancel</button>
              <button onClick={() => runBulk("soft_delete")} className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-700 bg-red-50" data-testid="fu-bulk-delete">Delete</button>
              <button onClick={() => setSelected(new Set())} className="text-xs text-slate-500">Clear</button>
            </div>
          </div>
        </div>
      )}

      <div className="mg-card overflow-x-auto p-0">
        <table className="w-full text-sm" data-testid="followups-table">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr>
              {canBulk && <th className="w-8 px-3 py-3"><input type="checkbox" onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r.followup_id)) : new Set())} data-testid="select-all-followups" /></th>}
              <th className="px-2 py-3">Patient</th>
              <th>Doctor</th>
              <th>Reason</th>
              <th>Due</th>
              <th>Priority</th>
              <th>Attempts</th>
              <th>Last contact</th>
              <th>Status</th>
              <th className="text-right">View</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="10" className="text-center text-slate-400 py-8">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan="10" className="text-center text-slate-400 py-8">No follow-ups found.</td></tr>}
            {!loading && rows.map((r) => {
              const overdue = isOverdue(r.due_date, r.status);
              return (
                <tr key={r.followup_id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`followup-row-${r.followup_id}`}>
                  {canBulk && <td className="px-3 py-3"><input type="checkbox" checked={selected.has(r.followup_id)} onChange={() => toggleSelect(r.followup_id)} data-testid={`fu-select-${r.followup_id}`} /></td>}
                  <td className="px-2 py-3">
                    <div className="font-semibold text-mg-navy">{r.patient?.name || "—"}</div>
                    <div className="text-[10px] text-slate-500">{r.patient?.phone || ""}</div>
                  </td>
                  <td className="text-xs">{r.doctor?.name || "—"}<div className="text-[10px] text-slate-500">{r.doctor?.specialization || ""}</div></td>
                  <td className="text-xs max-w-xs"><div className="truncate">{r.reason || "—"}</div></td>
                  <td className={`text-xs ${overdue ? "text-red-600 font-semibold" : "text-mg-navy"}`}>
                    {overdue && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                    {fmtDate(r.due_date)}
                  </td>
                  <td className={`text-xs ${PRIORITY_STYLES[r.priority] || ""}`}>{r.priority || "normal"}</td>
                  <td className="text-xs text-center">{r.contact_attempts_count || 0}</td>
                  <td className="text-xs">
                    {r.last_contact ? (
                      <div>
                        <div>{fmtDateTime(r.last_contact.attempted_at)}</div>
                        <div className="text-[10px] text-slate-500">{r.last_contact.channel} · {r.last_contact.outcome}</div>
                      </div>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[r.status] || ""}`}>{r.status?.replace(/_/g, " ")}</span></td>
                  <td className="text-right">
                    <Link to={`${homeBase}/followups/${r.followup_id}`} className="p-1.5 hover:bg-slate-100 rounded inline-block" data-testid={`fu-view-${r.followup_id}`}>
                      <ChevronRight className="w-4 h-4 text-mg-medical" />
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showCreate && <FollowUpCreateModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </DashboardLayout>
  );
}

// ============================================================
// CREATE MODAL
// ============================================================
function FollowUpCreateModal({ onClose, onCreated }) {
  return <ScheduleFollowupModal onClose={onClose} onSaved={onCreated} />;
}

// ============================================================
// DETAIL PAGE
// ============================================================
export function ClinicFollowupDetailPage({ workspaceLinks, homeBase="/clinic-admin" }) {
  const clinicLinks = useClinicLinks();
  const links = workspaceLinks || clinicLinks;
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [actionReasonModal, setActionReasonModal] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const canEdit = useCan("followups.edit");
  const canContact = useCan("followups.contact_patient");
  const canComplete = useCan("followups.mark_completed");
  const canMiss = useCan("followups.mark_missed");
  const canCancel = useCan("followups.cancel");
  const canConvert = useCan("followups.convert_to_appointment");
  const canDelete = useCan("followups.delete");
  const canRestore = useCan("followups.restore");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/clinic/followups-x/${id}/detail`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const doAction = async (path, label, reason, payload = {}) => {
    setActionBusy(true);
    try {
      await api.post(`/clinic/followups-x/${id}/${path}`, { reason: reason || null, ...payload });
      toast.success(label);
      setActionReasonModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
    setActionBusy(false);
  };
  const doDelete = async () => {
    if (!window.confirm("Soft-delete this follow-up?")) return;
    try { await api.delete(`/clinic/followups-x/${id}`); toast.success("Deleted"); nav(`${homeBase}/followups`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const doRestore = async () => {
    try { await api.post(`/clinic/followups-x/${id}/restore`); toast.success("Restored"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!data) return <DashboardLayout links={links} title="Follow-up"><div className="text-slate-500">Loading…</div></DashboardLayout>;

  const overdue = isOverdue(data.due_date, data.status);
  const isTerminal = ["completed", "cancelled", "converted_to_appointment", "deleted"].includes(data.status);

  return (
    <DashboardLayout links={links} title="Follow-up">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <button onClick={() => nav(`${homeBase}/followups`)} className="mg-btn-secondary text-sm" data-testid="fu-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex gap-2 flex-wrap">
          {canEdit && data.status !== "deleted" && (
            <button onClick={() => setEditOpen(true)} className="mg-btn-secondary text-sm" data-testid="fu-edit-btn">
              <Pencil className="w-4 h-4" /> Edit
            </button>
          )}
          {data.status !== "deleted" && canDelete && (
            <button onClick={doDelete} className="text-sm px-3 py-1.5 rounded-lg border border-red-200 text-red-700 bg-red-50" data-testid="fu-delete-btn">
              <Trash2 className="w-4 h-4 inline" /> Delete
            </button>
          )}
          {data.status === "deleted" && canRestore && (
            <button onClick={doRestore} className="mg-btn-secondary text-sm" data-testid="fu-restore-btn">Restore</button>
          )}
        </div>
      </div>

      <div className="mg-card mb-4">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-xl bg-mg-medical/10 text-mg-medical flex items-center justify-center shrink-0">
            <PhoneCall className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-2xl font-bold text-mg-navy" data-testid="fu-detail-reason">{data.reason || "Follow-up"}</h2>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[data.status] || ""}`} data-testid="fu-status-badge">{data.status?.replace(/_/g, " ")}</span>
              <span className={`text-xs ${PRIORITY_STYLES[data.priority] || ""}`}><Flag className="w-3 h-3 inline" /> {data.priority || "normal"}</span>
              {data.auto_created && <span className="text-[10px] uppercase bg-mg-medical/10 text-mg-medical px-2 py-0.5 rounded-full">auto</span>}
            </div>
            <div className="text-sm text-slate-600 mt-2">
              <span className="inline-flex items-center gap-1"><UserIcon className="w-3 h-3" /><strong>{data.patient?.name || "—"}</strong> · {data.patient?.phone || ""}</span>
              <span className="mx-2 text-slate-300">|</span>
              <span className="inline-flex items-center gap-1"><Stethoscope className="w-3 h-3" />{data.doctor?.name || "—"}</span>
            </div>
            <div className={`text-sm mt-1 ${overdue ? "text-red-600 font-semibold" : "text-slate-600"}`}>
              <CalendarClock className="w-3 h-3 inline" /> Due {fmtDate(data.due_date)} {overdue && "(overdue)"}
            </div>
            {data.notes && <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{data.notes}</p>}
          </div>
        </div>
      </div>

      {!isTerminal && (
        <div className="mg-card mb-4">
          <h3 className="font-bold text-mg-navy mb-3 text-sm uppercase tracking-wider">Actions</h3>
          <div className="flex flex-wrap gap-2" data-testid="fu-actions">
            {canContact && (
              <button onClick={() => setContactOpen(true)} className="mg-btn-primary text-sm" data-testid="fu-log-contact">
                <PhoneCall className="w-4 h-4" /> Log contact
              </button>
            )}
            {canContact && ["pending", "contacted"].includes(data.status) && (
              <button onClick={() => setConfirmOpen(true)} className="mg-btn-secondary text-sm" data-testid="fu-confirm-patient">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Patient agreed
              </button>
            )}
            {canConvert && data.status === "confirmed_pending_booking" && (
              <button onClick={() => setConvertOpen(true)} className="mg-btn-secondary text-sm" data-testid="fu-convert">
                <Repeat className="w-4 h-4" /> Convert to appointment
              </button>
            )}
            {canComplete && (
              <button onClick={() => setActionReasonModal({ path: "complete", label: "Marked completed" })} className="mg-btn-secondary text-sm" data-testid="fu-complete">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Complete
              </button>
            )}
            {canMiss && (
              <button onClick={() => setActionReasonModal({ path: "miss", label: "Marked missed" })} className="mg-btn-secondary text-sm" data-testid="fu-miss">
                <XCircle className="w-4 h-4 text-red-500" /> Missed
              </button>
            )}
            {canCancel && (
              <button onClick={() => setActionReasonModal({ path: "cancel", label: "Cancelled" })} className="mg-btn-secondary text-sm" data-testid="fu-cancel">
                <X className="w-4 h-4" /> Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {data.appointment && (
        <div className="mg-card mb-4 bg-mg-medical/5 border-mg-medical/20">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-xs uppercase tracking-wider text-mg-medical font-semibold">Converted to appointment</div>
              <div className="font-bold text-mg-navy">{fmtDateTime(data.appointment.appointment_datetime)}</div>
            </div>
            <span className="text-xs text-slate-500">#{data.appointment.appointment_id}</span>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="mg-card">
          <h3 className="font-bold text-mg-navy mb-3 flex items-center gap-2"><PhoneCall className="w-4 h-4" /> Contact attempts ({data.contacts?.length || 0})</h3>
          {(!data.contacts || data.contacts.length === 0) ? (
            <div className="text-slate-400 text-sm py-4 text-center">No contact attempts yet.</div>
          ) : (
            <ul className="space-y-3" data-testid="fu-contacts-list">
              {data.contacts.map((c) => {
                const Icon = CHANNEL_ICONS[c.channel] || MessageCircle;
                return (
                  <li key={c.contact_id} className="flex items-start gap-3 border-b border-slate-50 last:border-0 pb-2" data-testid={`fu-contact-${c.contact_id}`}>
                    <Icon className="w-4 h-4 text-mg-medical mt-1 shrink-0" />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-mg-navy">{c.channel} · <span className="text-slate-500 font-normal">{c.outcome}</span></div>
                      <div className="text-xs text-slate-500">{fmtDateTime(c.attempted_at)}</div>
                      {c.notes && <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{c.notes}</p>}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="mg-card">
          <h3 className="font-bold text-mg-navy mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Activity</h3>
          {(!data.activity || data.activity.length === 0) ? (
            <div className="text-slate-400 text-sm py-4 text-center">No activity yet.</div>
          ) : (
            <ul className="space-y-2 text-xs" data-testid="fu-activity-list">
              {data.activity.map((a, i) => (
                <li key={i} className="border-b border-slate-50 last:border-0 pb-1.5">
                  <div className="font-medium text-mg-navy">{a.action}</div>
                  <div className="text-slate-500">{fmtDateTime(a.created_at)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {contactOpen && <LogContactModal followupId={id} onClose={() => setContactOpen(false)} onLogged={() => { setContactOpen(false); load(); }} />}
      {confirmOpen && <FollowupConfirmModal followupId={id} onClose={() => setConfirmOpen(false)} onConfirmed={() => { setConfirmOpen(false); load(); }} />}
      {convertOpen && <FollowupConvertModal followupId={id} doctorId={data.doctor_id} clinicId={data.clinic_id} initialDate={data.due_date} onClose={() => setConvertOpen(false)} onConverted={() => { setConvertOpen(false); load(); }} />}
      {editOpen && <EditFollowupModal data={data} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); load(); }} />}
      <ActionPromptModal
        open={Boolean(actionReasonModal)}
        title={actionReasonModal ? `${actionReasonModal.label}: optional reason?` : "Action reason"}
        label="Reason"
        placeholder=""
        initialValue=""
        confirmLabel={actionReasonModal?.label || "Submit"}
        busy={actionBusy}
        onCancel={() => setActionReasonModal(null)}
        onConfirm={(reason) => {
          if (!actionReasonModal) return;
          void doAction(actionReasonModal.path, actionReasonModal.label, reason);
        }}
      />
    </DashboardLayout>
  );
}

// ============================================================
// LOG CONTACT MODAL
// ============================================================
function LogContactModal({ followupId, onClose, onLogged }) {
  const [form, setForm] = useState({ channel: "phone", outcome: "reached", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/clinic/followups-x/${followupId}/contact`, form);
      toast.success("Contact logged"); onLogged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setSubmitting(false);
  };
  return (
    <Modal title="Log contact attempt" onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Channel</label>
          <select className="mg-input w-full" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} data-testid="contact-channel">
            <option value="phone">Phone</option>
            <option value="sms">SMS</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="email">Email</option>
            <option value="in_person">In person</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Outcome</label>
          <select className="mg-input w-full" value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })} data-testid="contact-outcome">
            <option value="reached">Reached</option>
            <option value="no_answer">No answer</option>
            <option value="voicemail">Voicemail / left message</option>
            <option value="wrong_number">Wrong number</option>
            <option value="declined">Declined</option>
            <option value="rescheduled">Asked to reschedule</option>
            <option value="other">Other</option>
          </select>
        </div>
        <textarea className="mg-input w-full" rows={3} placeholder="Notes (optional)"
          value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="contact-notes" />
      </div>
      <div className="flex gap-2 mt-5">
        <button onClick={onClose} className="mg-btn-secondary flex-1 justify-center">Cancel</button>
        <button onClick={submit} disabled={submitting} className="mg-btn-primary flex-1 justify-center" data-testid="contact-submit">
          {submitting ? "Saving…" : "Log contact"}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// EDIT MODAL
// ============================================================
function EditFollowupModal({ data, onClose, onSaved }) {
  const [form, setForm] = useState({
    due_date: data.due_date || "",
    reason: data.reason || "",
    priority: data.priority || "normal",
    notes: data.notes || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try {
      const updates = { ...form };
      if (data.appointment_id || data.converted_to_appointment_id) delete updates.due_date;
      await api.patch(`/clinic/followups-x/${data.followup_id}`, updates);
      toast.success("Updated"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setSubmitting(false);
  };
  return (
    <Modal title="Edit follow-up" onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Due date</label>
          {(data.appointment_id || data.converted_to_appointment_id) && <p className="text-xs text-slate-500">To change the visit time, reschedule the linked appointment using available slots.</p>}
          <input type="date" disabled={!!(data.appointment_id || data.converted_to_appointment_id)} className="mg-input w-full" value={form.due_date}
            onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="edit-fu-due" />
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Reason</label>
          <input className="mg-input w-full" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="edit-fu-reason" />
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Priority</label>
          <select className="mg-input w-full" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="edit-fu-priority">
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
        <textarea className="mg-input w-full" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="edit-fu-notes" />
      </div>
      <div className="flex gap-2 mt-5">
        <button onClick={onClose} className="mg-btn-secondary flex-1 justify-center">Cancel</button>
        <button onClick={submit} disabled={submitting} className="mg-btn-primary flex-1 justify-center" data-testid="edit-fu-submit">
          {submitting ? "Saving…" : "Save"}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// SHARED MODAL
// ============================================================
function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-mg-navy text-lg">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
